import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";
import { pubsub } from "utils";
import { ContainerBroadcastEvent, getBridge } from "@/src/lib/bridge";

const TEN_DAYS_MS = 10 * 24 * 60 * 60 * 1000;

type TrackerReadMap = Record<string, number>;

type TrackerReadWatermarkBroadcastPayload = {
  ownerId: string;
  trackerId: string;
  readAt: number;
  source: "local_mark_read" | "realtime_sync";
};

const keyForOwner = (ownerId: string) => `tracker_read_watermark_v1:${ownerId}`;

function loadMap(ownerId: string): TrackerReadMap {
  if (typeof window === "undefined" || !ownerId) {
    return {};
  }
  try {
    const raw = localStorage.getItem(keyForOwner(ownerId));
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as TrackerReadMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveMap(ownerId: string, map: TrackerReadMap) {
  if (typeof window === "undefined" || !ownerId) {
    return;
  }
  localStorage.setItem(keyForOwner(ownerId), JSON.stringify(map));
}

function broadcastTrackerReadWatermarkUpdated(
  payload: TrackerReadWatermarkBroadcastPayload,
) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    getBridge().broadcastContainerEvent(
      ContainerBroadcastEvent.TRACKER_READ_WATERMARK_UPDATED,
      payload,
    );
  } catch {
    // Ignore bridge failures; local watermark + pubsub remain the source of truth in this WebView.
  }
}

export function getTrackerReadAt(ownerId: string, trackerId: string): number {
  const map = loadMap(ownerId);
  return Number(map[trackerId] ?? 0);
}

export function upsertTrackerReadWatermark(
  ownerId: string,
  trackerId: string,
  readAt: number,
  source: "local_mark_read" | "realtime_sync" = "local_mark_read",
): void {
  if (!ownerId || !trackerId || !Number.isFinite(readAt)) {
    return;
  }
  const map = loadMap(ownerId);
  map[trackerId] = Math.max(Number(map[trackerId] ?? 0), readAt);
  saveMap(ownerId, map);
  const payload = {
    ownerId,
    trackerId,
    readAt: map[trackerId],
    source,
  } satisfies TrackerReadWatermarkBroadcastPayload;
  pubsub.emit(pubSubKeyMap.TRACKER_READ_WATERMARK_UPDATED, payload);
  broadcastTrackerReadWatermarkUpdated(payload);
}

export function cleanupTrackerReadWatermark(ownerId: string, now = Date.now()) {
  const map = loadMap(ownerId);
  const cutoff = now - TEN_DAYS_MS;
  const next: TrackerReadMap = {};
  Object.entries(map).forEach(([trackerId, readAt]) => {
    if (Number(readAt) >= cutoff) {
      next[trackerId] = Number(readAt);
    }
  });
  saveMap(ownerId, next);
}
